import { ref } from 'vue'
import { defineStore } from 'pinia'
import { createKoraClient } from '@architeia/api-client'
import { findSupportedLocale } from '@architeia/utils'
import { SECRET_ORG_LOCALES, type SecretOrgLocale } from '@/i18n'
import { readLocaleHint, writeLocaleHint } from '@/i18n/localeHint'
import { useAuthStore } from '@/stores/auth'

/**
 * ADR-010 D3: the member's Kora account language and time zone. `locale` starts from the per-origin
 * hint and is replaced by Kora's value once `load()` resolves. Only `load()` writes the hint and
 * `clear()` removes it (sign-out).
 */
export const useAccountPreferencesStore = defineStore('accountPreferences', () => {
  const locale = ref<SecretOrgLocale | null>(readLocaleHint())
  const timeZone = ref<string | null>(null)

  async function load(): Promise<void> {
    const auth = useAuthStore()
    try {
      const user = await createKoraClient({
        getAccessToken: options => auth.getAccessToken(options)
      }).getCurrentUser()
      locale.value = findSupportedLocale(SECRET_ORG_LOCALES, user.locale)
      timeZone.value = typeof user.timeZone === 'string' && user.timeZone ? user.timeZone : null
      writeLocaleHint(locale.value)
    } catch {
      // Not provisioned yet, offline, or Kora unavailable: keep the hint and the browser language.
    }
  }

  function clear(): void {
    locale.value = null
    timeZone.value = null
    writeLocaleHint(null)
  }

  return { locale, timeZone, load, clear }
})
